import { AxiosError } from "axios";

import {
  Configuration,
  DocumentLineModel,
  FileUri,
  FileUriODataResultCollection,
  OrderRequestDropdowns,
  QuotationApi,
  QuotationApiApiQuotationDocEntryGetOrderRequestDropdownsGetRequest,
  QuotationLineApi,
  QuotationLineModelODataResultCollection,
  QuotationModel,
  QuotationModelODataResultCollection,
} from "api";
import {
  andFilters,
  ApiResponse,
  apiResponseTransformer,
  baseApiConfig,
  filterOnlyUniqueByKey,
  invalidStatusCode,
  listOfFilters,
  orFilters,
  removeNullable,
  sortToString,
  TransformedApiResponse,
  validateApiResponse,
} from "./apiHelpers";
import {
  API_RESPONSE_BROKEN_VALIDATION_EXCEPTION_TYPE,
  ApiStatusException,
  BrokenRuleValidationError,
  extractBrokenValidationError,
} from "services/apiErrors";
import {
  DEFAULT_EMPTY_VALUE,
  formatCurrency,
  formatNumber,
  formatQuantityValue,
  valueOr,
  valueToDate,
} from "util/statHelpers";
import ModeOfTransportationService from "./ModeOfTransportationService";
import filterFactories, { compare } from "./filterFactories";

export type FilterSetOption = {
  heading: string;
  options?: SelectOption[];
  filterKey: string;
};
type ContractListFiltersMock = {
  filterSets: FilterSetOption[];
  toggles: ToggleOption[];
};

export const contractListFilters: ContractListFiltersMock = {
  filterSets: [
    {
      heading: "Statuses",
      filterKey: "statuses",
      options: [],
    },
    {
      heading: "Products",
      filterKey: "products",
      options: [],
    },
  ],
  toggles: [
    {
      label: "Balance Due",
      filterKey: "pastDue",
    },
    {
      label: "Closed Contracts",
      filterKey: "closedContracts",
    },
  ],
};

export type ContractOrderRequestSelectedOptions = Pick<
  QuotationApiApiQuotationDocEntryGetOrderRequestDropdownsGetRequest,
  "modeOfTransportationCode" | "salesTermsCode" | "shipToCode" | "warehouseCode"
>;

// Spec doesn't combine QuotationModel allOf with extra properties
export type QuotationModelWithOrderStatus = QuotationModel & {
  IndicatorExpiringSoon?: boolean;
  IndicatorMoreOrdersRequired?: boolean;
  DisplayRequestOrders?: boolean;
  DaysRemaining?: number;
  ContractQuantityRemainingToOrder?: number;
  ContractAcceptanceUri?: string;
};

enum ContractFileCategories {
  Contract = "Contract",
  Statement = "Statement",
}

export type RequestContractValues = {
  poNumber?: string;
  product: string;
  quantity: number;
  shipDateEnd: string;
  shipDateStart: string;
  uomCode: string;
  contractLines: {
    shipFromCode: string;
    shipToCode: string;
    salesTermsCode: string;
    modeOfTransportationCode: string;
  }[];
};

export const UNSIGNED_CONTRACT_FILTER_STATUS = "Unsigned";

class ContractService {
  private quotationApi!: QuotationApi;
  private quotationLineApi!: QuotationLineApi;
  private modes: Record<string, ModeOfTransportation> | undefined;
  private modeService!: ModeOfTransportationService;

  constructor(config: Configuration = baseApiConfig) {
    this.quotationApi = new QuotationApi(config);
    this.quotationLineApi = new QuotationLineApi(config);
    this.modeService = new ModeOfTransportationService(config);
  }

  private async fetchModes() {
    this.modes = {};
    return this.modeService.listModesOfTransportation().then(({ value }) =>
      value.forEach((mode) => {
        if (mode.code && this.modes) {
          this.modes[mode.code] = mode;
        }
      })
    );
  }

  private mapModeOfTransportationCode(
    modeOfTransportationCode?: string | null
  ): ModeOfTransportation | typeof DEFAULT_EMPTY_VALUE {
    return modeOfTransportationCode && this.modes
      ? this.modes[modeOfTransportationCode]
      : valueOr(undefined);
  }

  private static mapApiContractValues(
    contract: QuotationModelWithOrderStatus
  ): Contract {
    const origins: SelectOption[] = [];
    const destinations: SelectOption[] = [];
    contract.DocumentLines?.forEach(
      ({ WarehouseCode, WarehouseName, Region }) => {
        WarehouseCode &&
          WarehouseName &&
          origins.push({
            label: WarehouseName,
            value: WarehouseCode,
          });
        Region &&
          destinations.push({
            label: Region,
            value: Region,
          });
      }
    );
    return {
      docEntry: contract.DocEntry,
      contractNumber: contract.ContractNumber,
      customerCode: valueOr(contract.CardCode),
      customerName: valueOr(contract.CardName),
      status: valueOr(contract.ContractStatus),
      origins:
        origins.length > 0
          ? removeNullable(origins).filter(filterOnlyUniqueByKey("value"))
          : undefined,
      destinations:
        destinations.length > 0
          ? removeNullable(destinations).filter(filterOnlyUniqueByKey("value"))
          : undefined,
      amountDue: valueOr(
        contract.CalculatedAmountDue
          ? formatCurrency(contract.CalculatedAmountDue, "$")
          : undefined
      ),
      productName: valueOr(contract.DocumentLines?.[0]?.ItemName), // Per IRM team the product name is the same for all lines
      itemCode: valueOr(contract.DocumentLines?.[0]?.ItemCode), // Per IRM team the product name is the same for all lines
      poNumber: valueOr(contract.NumAtCard),
      quantityShipped: contract.ContractQuantityDelivered ?? 0,
      quantityShippedFormatted: formatQuantityValue(
        contract.ContractQuantityDelivered
      ),
      quantityRemainingToOrder: contract.ContractQuantityRemainingToOrder ?? 0,
      quantityRemainingToOrderFormatted: formatQuantityValue(
        contract.ContractQuantityRemainingToOrder
      ),
      quantityTotal: contract.ContractQuantityCommitted ?? 0,
      quantityTotalFormatted: formatQuantityValue(
        contract.ContractQuantityCommitted
      ),
      quantityOnOrderOpen: contract.ContractQuantityOrderedOpen ?? 0,
      quantityOnOrderOpenFormatted: formatQuantityValue(
        contract.ContractQuantityOrderedOpen
      ),
      unitOfMeasure: contract.DocumentLines?.[0]?.UnitOfMeasureCode ?? "",
      shipStartDate: valueOr(valueToDate(contract.ShipDateStart)),
      shipEndDate: valueOr(valueToDate(contract.ShipDateEnd)),
      currency: valueOr(contract.Currency),
      expiringSoon: contract.IndicatorExpiringSoon,
      moreOrdersRequired: contract.IndicatorMoreOrdersRequired,
      canRequestMoreOrders: contract.DisplayRequestOrders,
      daysRemaining: contract.DaysRemaining,
      esignLink: contract.ContractAcceptanceUri,
      esignButtonVisible: contract.ContractAcceptanceShow,
    };
  }

  private static mapApiFileUri(
    fileUri: FileUri,
    category: string
  ): ContractFile {
    return {
      category,
      date: valueOr(fileUri.Date ? new Date(fileUri.Date) : undefined),
      filename: valueOr(fileUri.FileName),
      mimeType: valueOr(fileUri.Mime ?? "application/pdf"),
      url: fileUri.Uri ?? undefined,
    };
  }

  static buildContractListFilters = (
    cardCodes: string[],
    {
      statuses,
      signature,
      shipStartDate,
      shipEndDate,
      products,
    }: ContractListFilters
  ) => {
    const { compare, documentLine } = filterFactories;

    return andFilters([
      compare("CardCode").in(listOfFilters(cardCodes)),
      signature?.includes(UNSIGNED_CONTRACT_FILTER_STATUS)
        ? compare("ContractAcceptanceShow").eq(true)
        : null,
      statuses.length > 0
        ? compare("ContractStatus").in(listOfFilters(statuses))
        : null,
      shipStartDate
        ? compare("ShipDateStart").gte(shipStartDate.toISOString())
        : null,
      shipEndDate
        ? compare("ShipDateEnd").lte(shipEndDate.toISOString())
        : null,
      products.length > 0
        ? documentLine("ItemName").in(listOfFilters(products))
        : null,
    ]);
  };

  getContractList(
    cardCodes: string[],
    searchTerm: string,
    filters: ContractListFilters = { statuses: [], products: [] },
    sort?: { key: string; direction: string }
  ): Promise<
    TransformedApiResponse<QuotationModelODataResultCollection, Contract[]>
  > {
    return this.quotationApi
      .apiQuotationSearchGet({
        searchString: searchTerm,
        $filter: ContractService.buildContractListFilters(cardCodes, filters),
        $orderby: sortToString(sort),
        $expand: "DocumentLines,ContractTotals",
      })
      .then(apiResponseTransformer)
      .then((data) => {
        if (!data) {
          throw new Error("Contracts not found");
        } else if (invalidStatusCode(data.statusCode)) {
          throw new ApiStatusException(data.statusText, data.statusCode);
        }
        const contracts: Contract[] =
          data.value?.map(ContractService.mapApiContractValues) ?? [];
        return {
          ...data,
          value: contracts,
        };
      });
  }

  getContractListWithEsignAvailable(
    cardCode: string[]
  ): Promise<
    TransformedApiResponse<QuotationModelODataResultCollection, Contract[]>
  > {
    return this.quotationApi
      .apiQuotationGet({
        $filter: orFilters(cardCode.map(compare("CardCode").eq)),
      })
      .then(apiResponseTransformer)
      .then((data) => {
        if (!data) {
          throw new Error("Contracts not found");
        } else if (invalidStatusCode(data.statusCode)) {
          throw new ApiStatusException(data.statusText, data.statusCode);
        }
        const contracts: Contract[] =
          (data.value as QuotationModelWithOrderStatus[])?.map(
            ContractService.mapApiContractValues
          ) ?? [];
        return {
          ...data,
          value: contracts,
        };
      });
  }

  getContractDetails(docEntry: number): Promise<{ contract: Contract }> {
    return this.quotationApi
      .apiQuotationDocEntryGet({ docEntry, $expand: "ContractTotals" })
      .then(({ data, statusText, status }) => {
        if (!data) {
          throw new Error("Contract not found");
        } else if (invalidStatusCode(status)) {
          throw new ApiStatusException(statusText, status);
        }
        return { contract: ContractService.mapApiContractValues(data) };
      });
  }

  getContractFileList(
    docEntry: number,
    sort?: { key: keyof FileUri; direction: string }
  ): Promise<
    TransformedApiResponse<
      FileUriODataResultCollection,
      { contracts: ContractFile[]; statements: ContractFile[] }
    >
  > {
    return this.quotationApi
      .apiQuotationGetFileUriListDocEntryGet({
        docEntry,
        $top: "50",
        $orderby: sortToString(sort),
      })
      .then(apiResponseTransformer)
      .then((data) => {
        if (!data) {
          throw new Error("Files not found");
        } else if (invalidStatusCode(data.statusCode)) {
          throw new ApiStatusException(data.statusText, data.statusCode);
        }
        return {
          ...data,
          value: {
            contracts:
              data.value
                ?.filter(
                  (fileUri) =>
                    fileUri.Category === ContractFileCategories.Contract
                )
                ?.map((fileUri) =>
                  ContractService.mapApiFileUri(
                    fileUri,
                    ContractFileCategories.Contract
                  )
                ) ?? [],
            statements:
              data.value
                ?.filter(
                  (fileUri) =>
                    fileUri.Category === ContractFileCategories.Statement
                )
                ?.map((fileUri) =>
                  ContractService.mapApiFileUri(
                    fileUri,
                    ContractFileCategories.Statement
                  )
                ) ?? [],
          },
        };
      });
  }

  getDocumentContractFileList(
    docEntry: number
  ): Promise<ApiResponse<FileUriODataResultCollection>> {
    return this.quotationApi
      .apiQuotationGetFileUriListDocEntryGet({
        docEntry,
        $top: "50",
      })
      .then(apiResponseTransformer)
      .then((data) => {
        if (!data) {
          throw new Error("Files not found");
        } else if (invalidStatusCode(data.statusCode)) {
          throw new ApiStatusException(data.statusText, data.statusCode);
        }

        return {
          ...data,
          value: data.value ?? [],
        };
      });
  }

  mapApiContractLineItem = (line: DocumentLineModel): ContractTerm => {
    return {
      docEntry: line.DocEntry ?? 0,
      shipFrom: valueOr(line.WarehouseName),
      shipFromCode: valueOr(line.WarehouseCode),
      shipTo: valueOr(line.Region),
      mode: this.mapModeOfTransportationCode(line.ModeOfTransportationCode),
      salesTerms: valueOr(line.SalesTermsCode),
      price: valueOr(line.Price ? formatNumber(line.Price) : undefined),
      priceUnit: valueOr(line.UnitOfMeasureCode),
    };
  };

  async listContractTerms(
    docEntry: number,
    _filters: OrdersFilters = { shipFrom: [], shipTo: [], mode: [] },
    sort?: { key: string; direction: string }
  ): Promise<
    TransformedApiResponse<
      QuotationLineModelODataResultCollection,
      ContractTerms
    >
  > {
    if (!this.modes) {
      await this.fetchModes();
    }
    const { compare } = filterFactories;
    const { shipFrom, shipTo, mode } = _filters;
    const filters = andFilters([
      compare("DocEntry").eq(docEntry),
      andFilters([
        shipFrom.length > 0
          ? compare("WarehouseCode").in(listOfFilters(shipFrom))
          : null,
        shipTo.length > 0 ? compare("Region").in(listOfFilters(shipTo)) : null,
        mode.length > 0
          ? compare("ModeOfTransportationCode").in(listOfFilters(mode))
          : null,
      ]),
    ]);

    return this.quotationLineApi
      .apiQuotationLineGet({
        $filter: filters,
        $orderby: sortToString(sort),
      })
      .then(apiResponseTransformer)
      .then((data) => {
        if (!data) {
          throw new Error("Terms not found");
        } else if (invalidStatusCode(data.statusCode)) {
          throw new ApiStatusException(data.statusText, data.statusCode);
        }
        return {
          ...data,
          value: data.value?.map(this.mapApiContractLineItem) ?? [],
        };
      });
  }

  getContractOrderRequestSelectOptions(
    docEntry: number,
    selectedOptions: ContractOrderRequestSelectedOptions
  ): Promise<{ data: OrderRequestDropdowns }> {
    return this.quotationApi
      .apiQuotationDocEntryGetOrderRequestDropdownsGet({
        docEntry,
        ...selectedOptions,
      })
      .then(({ status, ...value }) => ({
        ...value,
        statusCode: status,
      }))
      .then(
        validateApiResponse("Contract order request options failed to load")
      );
  }

  getRequestContractSelectOptions(cardCode: string, itemCode: string) {
    return this.quotationApi
      .apiQuotationGetContractRequestDropdownsGet({
        cardCode,
        itemCode,
      })
      .then(({ status, ...value }) => ({
        ...value,
        statusCode: status,
      }))
      .then(validateApiResponse("Contract line item options failed to load"));
  }

  requestContract(cardCode: string, values: RequestContractValues) {
    return this.quotationApi
      .apiQuotationRequestContractPost({
        apiQuotationRequestContractPostRequest: {
          CardCode: cardCode,
          ItemCode: values.product,
          NumAtCard: values.poNumber,
          ShipDateStart: values.shipDateStart,
          ShipDateEnd: values.shipDateEnd,
          UnitOfMeasureCode: values.uomCode,
          Quantity: values.quantity,
          ContractLines: values.contractLines.map((contractLine) => ({
            WarehouseCode: contractLine.shipFromCode,
            ShipToCode: contractLine.shipToCode,
            SalesTermsCode: contractLine.salesTermsCode,
            ModeOfTransportationCode: contractLine.modeOfTransportationCode,
          })),
        },
      })
      .then((response) => response.data)
      .catch((err: AxiosError<BrokenRuleValidationError>) => {
        if (err.response?.data) {
          const brokenValidationError = extractBrokenValidationError(err);
          if (
            brokenValidationError?.Type ===
            API_RESPONSE_BROKEN_VALIDATION_EXCEPTION_TYPE
          ) {
            throw brokenValidationError;
          }
        } else if (err.code === "ERR_NETWORK") {
          throw new Error(err.message);
        }
        throw err;
      });
  }
}

export default ContractService;
